import React, { useContext, useEffect } from 'react';
import {
  ExperienceContext,
  useConfigService,
} from '@thd-nucleus/experience-context';
import { Alert } from '@one-thd/sui-atomic-components';
import { usePayments } from '../../../hooks/use-payments';
import { useLoyaltyMembershipDetails } from '../../../hooks/use-loyalty-membership-details';
import { Spinner } from '../../shared/spinner/spinner';
import { CardType } from '../../../constants/payments';
import { PaymentsHeader } from '../payments-header/payments-header';
import { LineOfCredit } from '../pro-house-acount/line-of-credit';
import { TAlertStatus } from '../../../models/common/alert-messages';
import { PROHOUSE_ACCOUNTS_NO_T2C_AUTHORIZED_TENDER_MSG } from '../../../constants/response-warning-messages';
import { shouldShowNoT2CEnrolledTenderWarning } from '../../../utils/pro-house-account';
import { useCustomerInfo } from '../../../hooks/use-customerInfo';
import { CustomerRole } from '../../../models/common/customer';
import { Pagination } from '../../shared/pagination/pagination';
import { PAYMENTS_PAGE_SIZE } from '../../../constants/general';
import { scrollToTop } from '../../../utils/general';

export const ProHouseAccountList = () => {
  const {
    loading,
    linesOfCredit,
    alertMessage,
    setAlertMessage,
    t2cCreditCards,
    paginationInfo,
    currentPageNumber,
    getPaymentCards,
  } = usePayments();
  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';
  const { role } = useCustomerInfo();
  const isAdmin = role === CustomerRole.ADMIN;
  const isPHAPermissionsEnabled = !!useConfigService('ENABLE_PHA_PERMISSIONS');
  const { hasPTCOnline } = useLoyaltyMembershipDetails();

  useEffect(() => {
    return () => {
      setAlertMessage(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleError = (paymentId: string, message: string) => {
    setAlertMessage({
      paymentId,
      alertStatus: TAlertStatus.ERROR,
      text: message,
    });
  };

  const handleSuccess = (paymentId: string, message: string) => {
    setAlertMessage({
      paymentId,
      alertStatus: TAlertStatus.SUCCESS,
      text: message,
    });
  };

  const successMessage = alertMessage &&
    alertMessage.alertStatus === TAlertStatus.SUCCESS && (
      <div className={isMobile ? 'sui-mt-4' : 'lg:sui-mb-8'}>
        <Alert
          size={isMobile ? 'small' : 'large'}
          data-testid="successMsg"
          status={alertMessage.alertStatus}
          onClose={() => setAlertMessage(null)}
        >
          {alertMessage.text}
        </Alert>
      </div>
    );
  const errorMessage = alertMessage &&
    alertMessage.alertStatus === TAlertStatus.ERROR && (
      <div className={isMobile ? 'sui-mt-4' : 'lg:sui-mb-8'}>
        <Alert
          data-testid="errorMsg"
          status={alertMessage.alertStatus}
          onClose={() => setAlertMessage(null)}
        >
          {alertMessage.text}
        </Alert>
      </div>
    );

  const shouldShowAlert =
    !!t2cCreditCards &&
    !!linesOfCredit?.paymentCards &&
    shouldShowNoT2CEnrolledTenderWarning(
      t2cCreditCards,
      linesOfCredit.paymentCards,
    );

  const infoMessage = shouldShowAlert && (
    <div className={isMobile ? 'sui-mt-4' : 'lg:sui-mb-8'}>
      <Alert data-testid="infoMsg" status={TAlertStatus.WARNING}>
        {PROHOUSE_ACCOUNTS_NO_T2C_AUTHORIZED_TENDER_MSG.message}
      </Alert>
    </div>
  );

  const totalPages = Number(paginationInfo?.totalPages) || 0;

  const onChangePage = (newPageNumber: number) => {
    getPaymentCards(CardType.PRO_HOUSE_ACCOUNT, false, false, {
      page: newPageNumber,
      pageSize: PAYMENTS_PAGE_SIZE,
    }).catch(() => {
      // errors are surfaced via the existing alertMessage/errorMessage state
    });
  };

  return (
    <div data-testid="proHouseAccountContent">
      <Spinner active={loading} />
      <PaymentsHeader
        cardType={CardType.PRO_HOUSE_ACCOUNT}
        onActionFailure={handleError}
        onActionSuccess={handleSuccess}
      />
      {infoMessage}
      {successMessage}
      {errorMessage}

      <div className="sui-grid sui-grid-cols-1 sui-gap-y-8 sui-gap-x-8 md:sui-grid-cols-2 2xl:sui-gap-y-16 2xl:sui-grid-cols-3">
        {linesOfCredit &&
          linesOfCredit.paymentCards.map((card) => {
            return (
              <div key={card.paymentId}>
                <LineOfCredit
                  card={card}
                  onActionFailure={handleError}
                  onActionSuccess={handleSuccess}
                  shouldDisableProjectLOC={shouldShowAlert}
                  isAdmin={isAdmin}
                  hasPTCPermissions={isPHAPermissionsEnabled && hasPTCOnline}
                />
              </div>
            );
          })}
      </div>

      {totalPages > 1 && (
        <div className="sui-w-full sui-justify-center sui-p-5 sui-flex">
          <Pagination
            nPages={totalPages}
            onChangePage={(newPage) => {
              onChangePage(newPage);
              setAlertMessage(null);
              scrollToTop();
            }}
            page={currentPageNumber}
          />
        </div>
      )}
    </div>
  );
};
